/**
 * Persistent offline write queue.
 * Operations are stored in localStorage under a per-user key.
 * When the browser comes back online, useOfflineSync flushes them.
 */

export type QueuedOperationType = 'insert' | 'update' | 'delete';

export interface QueuedOperation {
  id: string;
  table: string;
  type: QueuedOperationType;
  payload: Record<string, unknown>;
  recordId?: string;
  userId: string;
  timestamp: number;
  retries: number;
}

const MAX_RETRIES = 3;
const MAX_QUEUE_SIZE = 100;

function storageKey(userId: string): string {
  return `offline_queue_${userId}`;
}

function save(userId: string, queue: QueuedOperation[]): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(queue));
  } catch {
    // localStorage full — drop oldest half
    localStorage.setItem(storageKey(userId), JSON.stringify(queue.slice(-50)));
  }
}

export function queueGetAll(userId: string): QueuedOperation[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    return raw ? (JSON.parse(raw) as QueuedOperation[]) : [];
  } catch {
    return [];
  }
}

export function queueAdd(
  userId: string,
  op: Omit<QueuedOperation, 'id' | 'userId' | 'timestamp' | 'retries'>
): QueuedOperation {
  const queue = queueGetAll(userId);
  if (queue.length >= MAX_QUEUE_SIZE) queue.shift();
  const entry: QueuedOperation = {
    ...op,
    id: crypto.randomUUID(),
    userId,
    timestamp: Date.now(),
    retries: 0,
  };
  queue.push(entry);
  save(userId, queue);
  return entry;
}

export function queueRemove(userId: string, opId: string): void {
  save(userId, queueGetAll(userId).filter(op => op.id !== opId));
}

export function queueIncrementRetry(userId: string, opId: string): void {
  const updated = queueGetAll(userId).map(op =>
    op.id === opId ? { ...op, retries: op.retries + 1 } : op
  );
  save(userId, updated.filter(op => op.retries <= MAX_RETRIES));
}

export function queueCount(userId: string): number {
  return queueGetAll(userId).length;
}

export function queueClear(userId: string): void {
  localStorage.removeItem(storageKey(userId));
}
